import { useState } from 'react';
import { get } from 'lodash';
import { useRecoilValue } from 'recoil';

import { LogEntry, QueryRequestInput, QueryType } from 'common/graphqlTypes';
import * as K from 'common/constants';
import * as T from 'common/types';

import { convertTimestampToMilliseconds, getDurationText, timezoneMoment } from 'common/util/time';
import { generateQueriesResultNode, LOG_ENTRY_FRAGMENT } from 'hooks/useLRQ/useLRQ.queries';
import useLRQ from 'hooks/useLRQ/useLRQ';
import ErrorPlaceholder from 'components/common/ErrorPlaceholder/ErrorPlaceholder';
import LoadingPlaceholder from 'components/common/LoadingPlaceholder/LoadingPlaceholder';
import { timezoneState } from 'components/common/Managers/PreferencesManager.state';

import { convertResultsToTraceData } from './TraceDetails.functions';
import TraceSpan from './TraceSpan';
import TraceTimeLines from './TraceTimeLines';

import './TraceDetails.scss';

const TRACE_TIME_WINDOW = 10 * K.MS_PER_MIN;
const MAX_TRACE_RESULTS = 1000;
type TraceDetailsProps = {
  logLine?: LogEntry;
};

/**
 * Get title for the current trace.
 *
 */
// TODO: Create type for trace
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getTraceTitle = (trace: any): string => {
  let title = 'Distributed Trace';
  const operation = get(trace, '[0].operation');
  if (operation) {
    title = `${title} of ${operation}`;
  }
  return title;
};

/**
 * This component shows details of a given log line. Details are shown as waterfall graph containing
 * all sub spans of the trace.
 * @param logLine - Log line to show the trace details for
 */
const TraceDetails = ({ logLine }: TraceDetailsProps): JSX.Element => {
  // TODO: Move this request generation logic to useLRQ?
  let rawMessage;
  try {
    const message = get(logLine, 'values.message', '{}');
    rawMessage = JSON.parse(message);
  } finally {
    // do nothing
  }

  const timezone = useRecoilValue(timezoneState);
  const timestamp = get(logLine, 'timestamp', 0);
  const timestampMs = convertTimestampToMilliseconds(timestamp);
  const traceId = get(logLine, 'values.traceId') || (rawMessage && rawMessage.baggage && rawMessage.baggage.traceId);
  const filter = `"spanId" '"traceId":"${traceId}"'`;
  const [selectedSpan, setSelectedSpan] = useState('');
  const traceDataRequest = {
    queriesResultNode: generateQueriesResultNode([LOG_ENTRY_FRAGMENT]),
    queriesVariables: [
      {
        ascending: false,
        startTime: timestampMs - TRACE_TIME_WINDOW,
        endTime: timestampMs + TRACE_TIME_WINDOW,
        filter,
        numResults: MAX_TRACE_RESULTS,
        offset: null,
        type: QueryType.LOG,
      } as unknown as QueryRequestInput,
    ],
  };

  const { result } = useLRQ(traceDataRequest);

  if (!('status' in result)) {
    return <></>;
  }

  if (result.status === K.QueryStatus.Terminated) {
    const error = {
      message: 'There was an error fetching Trace Data.',
    };
    return <ErrorPlaceholder error={error} />;
  }

  if (result.status === K.QueryStatus.Running) {
    return <LoadingPlaceholder />;
  }
  let traceDetails: Array<T.Span> = [];

  if ('results' in result) {
    const logEntries = get(result, 'results[0].data.matches');
    traceDetails = convertResultsToTraceData(logEntries);
  }
  const traceDuration = traceDetails[0].endTime - traceDetails[0].startTime;
  const durationText = getDurationText(traceDuration, traceDuration >= 1000 ? K.DATE.SECOND : K.DATE.MILLISECOND);

  const spans = traceDetails.map((span) => (
    <TraceSpan
      span={span}
      traceStartTime={traceDetails[0].startTime}
      traceDuration={traceDuration}
      detailsVisible={span.spanId === selectedSpan}
      setSpanSelected={(spanId: string): void => {
        setSelectedSpan(spanId !== selectedSpan ? spanId : '');
      }}
    />
  ));

  const startTime = timezoneMoment(timezone)(get(traceDetails, '[0].startTime')).format('LLL');

  return (
    <div className="TraceDetails">
      <div className="TraceDetails__Header">
        <div className="TraceDetails__Title">{getTraceTitle(traceDetails)}</div>
        <div className="TraceDetails__TraceTiming">
          <span>Start Time: </span>
          <span className="TraceDetails__StartTime">{startTime}</span>
          <span>Duration: </span>
          <span className="TraceDetails__TraceDuration">{durationText}</span>
        </div>
      </div>
      <div className="TraceDetails__Span">
        {spans}
        <div className="TraceDetails__TraceTimeLineBackground" />
        <TraceTimeLines traceDuration={traceDuration} />
      </div>
    </div>
  );
};

export default TraceDetails;
